import React, { type FC, useState } from 'react';
import { PromotionalList, Text, Preloader } from '@wix/wix-base-ui';
import { clickOnPanel, panelHeaderClose } from '@wix/bi-logger-editor/v2';
import { hoc, biLogger } from '#packages/util';
import { frames } from '#packages/panels';
import { translate } from '#packages/i18n';
import { symbol as Symbol } from '@wix/santa-editor-symbols';

import { PANEL_CONTENT } from './constants';
import {
  mapDispatchToProps,
  type DispatchProps,
} from './optionalSectionsMigrationPanelMapper';
import styles from './optionalSectionsMigrationPanel.scss';
import type { PanelProps } from '#packages/stateManagement';
import { MigrationOrigin } from '#packages/sectionsMigration';

interface OptionalSectionsMigrationPanelOwnProps extends PanelProps {
  origin: MigrationOrigin.SiteMenu | MigrationOrigin.TopBarBanner;
}

export interface OptionalSectionsMigrationPanelProps
  extends OptionalSectionsMigrationPanelOwnProps,
    DispatchProps {}

const OptionalSectionsMigrationPanel: FC<
  OptionalSectionsMigrationPanelProps
> = ({ origin, panelName, openHelpCenter, closePanel, forceMigrate }) => {
  const [isLoading, setIsLoading] = useState(false);

  const reportClick = (clickOrigin: string) => {
    biLogger.report(
      clickOnPanel({
        action_name: `${clickOrigin}_btn`,
        panel_name: panelName,
        origin, // from where was the panel opened
      }),
    );
  };

  const reportClose = () => {
    biLogger.report(
      panelHeaderClose({
        panel_name: panelName,
        origin: 'X',
      }),
    );
  };

  const onPrimaryButtonClick = () => {
    setIsLoading(true);
    reportClick('update_editor');
    forceMigrate({
      origin,
    });
  };
  const onCloseButtonClick = () => {
    reportClick('close');
    reportClose();
    closePanel(panelName);
  };

  const onSecondaryButtonClick = () => {
    reportClick('learn_more');
    openHelpCenter();
  };

  const onHelpButtonClick = () => {
    reportClick('help');
    openHelpCenter();
  };

  return (
    <frames.PromotionalPanelFrame
      panelName={panelName}
      dataHook="optional-sections-migration-panel"
      title={translate(PANEL_CONTENT.title)}
      illustration={
        <Symbol
          name="topBarSiteMenuUpdateEditor"
          className={styles.illustration}
        />
      }
      // subtitle={translate(PANEL_CONTENT.subtitle)}
      primaryButtonText={
        isLoading ? ' ' : translate(PANEL_CONTENT.primaryButtonText)
      }
      primaryButtonProps={
        isLoading ? { prefixIcon: <Preloader className="tiny" /> } : undefined
      }
      secondaryButtonText={translate(PANEL_CONTENT.secondaryButtonText)}
      onPrimaryButtonClick={onPrimaryButtonClick}
      onSecondaryButtonClick={onSecondaryButtonClick}
      onCloseButtonClick={onCloseButtonClick}
      onHelpButtonClick={onHelpButtonClick}
      footnote={
        <Text enableEllipsis={false} size="small" skin="standard">
          {PANEL_CONTENT.footnote}
        </Text>
      }
      className={isLoading ? styles.loading : undefined}
    >
      <div className={styles.listContainer}>
        <PromotionalList
          items={PANEL_CONTENT.items}
          symbol={<Symbol className={styles.check} name="check" />}
        />
      </div>
    </frames.PromotionalPanelFrame>
  );
};

export default hoc.connect(
  hoc.STORES.EDITOR_API,
  null,
  mapDispatchToProps,
)(OptionalSectionsMigrationPanel);
